import { useState } from 'react';
import { Shield, Lock, ArrowLeft, Mail, PhoneOutgoing } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useRequestSubscriptionUpgrade } from '@/hooks/queries/useUser';

export function UpgradeRequired({
  title = 'Upgrade Required',
  description = 'Please upgrade your subscription to access this feature.',
  backHref = '/',
  defaultDesired = 'pro',
}: {
  title?: string;
  description?: string;
  backHref?: string;
  defaultDesired?: 'pro' | 'enterprise';
}) {
  const [desiredPlan, setDesiredPlan] = useState<'pro' | 'enterprise'>(defaultDesired);
  const [message, setMessage] = useState('');

  const requestUpgrade = useRequestSubscriptionUpgrade();

  const handleRequestUpgrade = () => {
    requestUpgrade.mutate({ desiredSubscription: desiredPlan, message: message || undefined });
  };

  return (
    <div className="min-h-[70vh] bg-gradient-to-b from-neutral-50 to-white">
      <div className="container mx-auto max-w-3xl px-4 py-12">
        <Card className="border border-neutral-200 shadow-md overflow-hidden">
          <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500" />
          <CardHeader className="pb-4">
            <div className="flex items-start gap-4">
              <div className="shrink-0 rounded-xl bg-amber-50 p-3 ring-1 ring-amber-100">
                <Lock className="h-6 w-6 text-amber-600" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-2xl text-neutral-900">{title}</CardTitle>
                <CardDescription className="text-neutral-700">
                  {description}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            {!requestUpgrade.isSuccess ? (
              <div className="space-y-6">
                <div className="rounded-lg border border-neutral-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <Shield className="h-4 w-4 text-amber-600" />
                    <span>Upgrade to unlock secure access and collaboration features.</span>
                  </div>
                  <ul className="mt-3 list-disc list-inside text-sm text-neutral-700 space-y-1">
                    <li>Advanced features for professional workflows</li>
                    <li>Priority support from our team</li>
                    <li>Seamless collaboration tools</li>
                  </ul>
                </div>

                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <div className="flex items-center gap-2">
                    <label htmlFor="plan" className="text-sm font-medium text-neutral-800 w-20">Plan</label>
                    <select
                      id="plan"
                      className="flex-1 h-10 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-800"
                      value={desiredPlan}
                      onChange={(e) => setDesiredPlan(e.target.value as 'pro' | 'enterprise')}
                    >
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      disabled={requestUpgrade.isPending}
                      onClick={handleRequestUpgrade}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      {requestUpgrade.isPending ? 'Sending...' : 'Request Upgrade'}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="note" className="text-sm font-medium text-neutral-800">Add a note (optional)</label>
                  <Textarea
                    id="note"
                    placeholder="Tell us what you need or any timeline considerations"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[90px]"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-emerald-800 text-sm">
                    Thanks! Your upgrade request has been sent. Our team will reach out shortly.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <a
                href={`mailto:laura.mcgee@arterra.ai?subject=Subscription%20Upgrade%20Request%20(${desiredPlan})`}
                className="inline-flex items-center gap-2 text-neutral-700 hover:text-neutral-900"
              >
                <Mail className="h-4 w-4" /> Contact support
              </a>
              <span className="text-neutral-300">•</span>
              <button
                onClick={handleRequestUpgrade}
                disabled={requestUpgrade.isPending}
                className="inline-flex items-center gap-2 text-neutral-700 hover:text-neutral-900"
              >
                <PhoneOutgoing className="h-4 w-4" /> Request a callback
              </button>
            </div>

            <a href={backHref} className="inline-flex">
              <Button variant="outline" className="border-neutral-200 text-neutral-700 hover:bg-neutral-50">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to Projects
              </Button>
            </a>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
